"use client";

import { forwardRef } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { stallAdviceKey } from "../utils/stall-advice";
import { noteKey, type UploadNote } from "../utils/upload-notes";

/**
 * The notes about just-picked photos, at the top of the photo section.
 * Informational and dismissible: never a modal, never in the way of the next
 * pick. See utils/upload-notes.ts.
 */
const UploadNotes = forwardRef<
  HTMLDivElement,
  {
    notes: UploadNote[];
    /**
     * Inside a social app's WebView (Facebook, Instagram, ...). Only there is
     * "open the page in Chrome or Safari" good advice: in Safari itself it
     * blames the wrong thing.
     */
    inApp: boolean;
    onDismiss: (key: string) => void;
  }
>(function UploadNotes({ notes, inApp, onDismiss }, ref) {
  const translate = useAppTranslate();
  if (notes.length === 0) return null;

  // Counts only, never file names: customers do not recognise
  // "IMG_1009.jpeg" as one of their photos (owner's decision).
  const textOf = (note: UploadNote): string => {
    switch (note.kind) {
      case "unreadable":
        return translate("order.noteUnreadable", { count: note.names.length });
      case "repick":
        return translate("order.noteRepick", { count: note.names.length });
      case "stalled":
        return translate(stallAdviceKey(), { count: note.names.length });
      case "message":
        return note.text;
    }
  };

  const hintsBrowser = (note: UploadNote) =>
    inApp && (note.kind === "unreadable" || note.kind === "repick");

  return (
    <Box ref={ref} sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      {notes.map((note) => {
        const key = noteKey(note);
        return (
          <Alert
            key={key}
            severity="info"
            role="status"
            closeText={translate("order.noteClose")}
            onClose={() => onDismiss(key)}
            sx={{ alignItems: "flex-start" }}
          >
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {textOf(note)}
            </Typography>
            {hintsBrowser(note) && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {translate("order.noteInAppHint")}
              </Typography>
            )}
          </Alert>
        );
      })}
    </Box>
  );
});

export default UploadNotes;
